import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import driverRouteService from '../../services/driverRouteService';
import { getFriendlyErrorMessage } from '../../utils/errorHandler';
import { ROUTES } from '../../routes';
import RouteStatusBadge from '../Rotas/RouteStatusBadge';
import { ROTEIRO_PONTO_INICIO } from './roteiroNavigation';
import {
    EmptyMessage,
    FieldValue,
    PageTitle,
    PageWrap,
    PrimaryActionButton,
    RoteiroCard,
    RoteiroCardMeta,
    RoteiroCardTitle,
    RoteiroCards,
} from './Roteiros.styles';

const PAGE_SIZE = 20;

export default function RoteirosListPage() {
    const navigate = useNavigate();
    const [roteiros, setRoteiros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (page === 0) {
                setLoading(true);
                setError(null);
            } else {
                setLoadingMore(true);
            }

            try {
                const result = await driverRouteService.list({
                    page,
                    size: PAGE_SIZE,
                    sort: 'id,desc',
                });
                if (cancelled) return;

                const content = result.content || [];
                setRoteiros((prev) => (page === 0 ? content : [...prev, ...content]));
                setHasMore(!result.page?.last);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                if (page === 0) {
                    setRoteiros([]);
                    setError(getFriendlyErrorMessage(err) || 'Não foi possível carregar os roteiros.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [page, reloadTick]);

    const handleRetry = useCallback(() => {
        setPage(0);
        setReloadTick((value) => value + 1);
    }, []);

    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            setPage((prev) => prev + 1);
        }
    }, [hasMore, loadingMore]);

    const handleSelectRoteiro = (roteiro) => {
        if (!roteiro?.pointCount) return;
        navigate(ROUTES.ROTEIROS_PONTO(roteiro.id, ROTEIRO_PONTO_INICIO));
    };

    return (
        <PageWrap>
            <PageTitle>Roteiros</PageTitle>

            {loading ? (
                <FieldValue>Carregando roteiros…</FieldValue>
            ) : null}

            {!loading && error ? (
                <>
                    <FieldValue style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</FieldValue>
                    <PrimaryActionButton type="button" onClick={handleRetry}>
                        Tentar novamente
                    </PrimaryActionButton>
                </>
            ) : null}

            {!loading && !error && roteiros.length === 0 ? (
                <EmptyMessage>Nenhum roteiro atribuído no momento.</EmptyMessage>
            ) : null}

            {!loading && !error && roteiros.length > 0 ? (
                <>
                    <RoteiroCards>
                        {roteiros.map((roteiro) => (
                            <RoteiroCard
                                key={roteiro.id}
                                type="button"
                                disabled={!roteiro.pointCount}
                                onClick={() => handleSelectRoteiro(roteiro)}
                            >
                                <RoteiroCardTitle>{roteiro.alias}</RoteiroCardTitle>
                                <RoteiroCardMeta>
                                    {roteiro.pointCount} pontos
                                </RoteiroCardMeta>
                                <RouteStatusBadge state={roteiro.state} />
                            </RoteiroCard>
                        ))}
                    </RoteiroCards>

                    {hasMore ? (
                        <PrimaryActionButton
                            type="button"
                            disabled={loadingMore}
                            onClick={handleLoadMore}
                            style={{ marginTop: 16, width: '100%' }}
                        >
                            {loadingMore ? 'Carregando…' : 'Carregar mais'}
                        </PrimaryActionButton>
                    ) : null}
                </>
            ) : null}
        </PageWrap>
    );
}
